import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { F90H } from '../../services/p930hApi';
import { useP930HMode } from './hooks/useP930HMode';
import { useP930HRecord } from './hooks/useP930HRecord';
import { useP930HNavigation } from './hooks/useP930HNavigation';
import { useP930HPermissions } from './hooks/useP930HPermissions';
import { useCalendarQuery } from './hooks/useCalendarQuery';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1EventForm from './components/Tab1EventForm';
import Tab2CalendarView from './components/Tab2CalendarView';
import ActionBar from './components/ActionBar';
import { isFieldDisabledFn, dateToInput } from './helpers';
import './P930HForm.css';

const P930HForm: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useP930HPermissions();
  const { mode, setMode, isEditing } = useP930HMode();
  const {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = useP930HRecord();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useP930HNavigation(
    useCallback(async (dateStr: string) => {
      await loadRecord(dateStr);
    }, [loadRecord])
  );

  const {
    year, setYear, month, setMonth,
    calMode, setCalMode,
    events, queryLoading, queried,
    executeQuery,
  } = useCalendarQuery();

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load P930H list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecord, setCurrentIdx]);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: F90H) => {
        if (mode === 'add') {
          const ids = await loadList();
          const dateKey = dateToInput(result.f90h_01);
          const newIdx = ids.indexOf(dateKey);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
      setActiveTab(0);
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除 ${dateToInput(record.f90h_01)} 的行事曆事件？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecord(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecord, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const isFieldDisabled = useCallback(
    (key: string) => isFieldDisabledFn(key, isEditing, mode),
    [isEditing, mode]
  );

  // Render
  return (
    <PermissionGuard module={Modules.P930H} action="query">
    <div className="p930h-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>人事管理</a>
              <span>/</span>
              <span className="current">公司行事日曆</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>公司行事日曆 Company Calendar</h1>
            <span className="badge badge-default">PW930H</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['事件建檔', '行事曆查詢'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: Event CRUD */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1EventForm
              formData={formData}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              getFieldError={getFieldError}
            />
          </div>

          {/* Tab 2: Calendar Query */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2CalendarView
              year={year}
              setYear={setYear}
              month={month}
              setMonth={setMonth}
              calMode={calMode}
              setCalMode={setCalMode}
              events={events}
              queryLoading={queryLoading}
              queried={queried}
              onQuery={executeQuery}
            />
          </div>
        </div>

        {/* Bottom Action Bar */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />
      </div>
    </div>
    </PermissionGuard>
  );
};

export default P930HForm;
